import { v4 as uuidv4 } from 'uuid';

import dateService from '@gem/services/date-service';

import Tasks from 'APP/Tasks';
import { UpdateReason } from 'APP/constants/scroll';
import { IForwardRequest, IMessage } from 'APP/model/message/messageModel.types';
import { messageModel } from 'APP/model/model';
import { ChatMessage } from 'APP/store/Messages/Message/ChatMessage/ChatMessage';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { MessageError } from 'STORE/Messages/Message/ChatMessage/ChatMessages.types';

import { updateOldOrFakeMessages } from '../updateOldOrFakeMessages/updateOldOrFakeMessages';

/**
 * Generates key for storing clientUuid for forwarded message
 * @param groupId id from a group where a massage is being forwarded
 * @param messageId id of a message being forwarded
 * @returns generated key for storing clientUuid for forwarded message
 */
function getHashMessageIdsKey(groupId: string, messageId: string): string {
  return groupId + messageId;
}

/**
 * Generates key for forwarded message uuid in a set
 * @param groupId id from a group where a massage is being forwarded
 * @param clientUuid id of a forwarded message
 * @returns generated key for forwarded message uuid in a set
 */
function getCreatedMessagesUUIDKey(groupId: string, clientUuid: string): string {
  return clientUuid + groupId;
}

/**
 * Transforms ChatMessage data to data which contains forwarded information
 * @param chatMessage a message which is being forwarded
 * @param index of a message in array
 * @param sourceGroup a group from a massage is being forwarded
 * @param targetGroupId id from a group where a massage is being forwarded
 * @param clientUuid id of forwarded message
 * @param dateNow current date of forwarded message
 * @returns ChatMessage with data which contains forwarded information
 */
function mapChatMessageToForwardedMessage(
  chatMessage: ChatMessage,
  index: number,
  sourceGroup: Group | null,
  targetGroupId: string,
  clientUuid: string,
  dateNow: number
): IMessage | null {
  const me = Entities.usersStore.Me;
  const sender = Entities.usersStore.getUserById(chatMessage?.senderId);

  if (!chatMessage || !sender || !sourceGroup) {
    return null;
  }

  return {
    clientUuid: clientUuid,
    forwardedMessage: chatMessage.forward
      ? {
          ...chatMessage.forward.toJSON(),
          messageId: chatMessage.id,
          clientGroupSourceId: sourceGroup.id,
        }
      : {
          from: sourceGroup.isChannel ? sourceGroup.name : sender.displayName,
          groupId: sourceGroup.isChannel ? sourceGroup.id : undefined,
          messageId: chatMessage.id,
          senderId: chatMessage.senderId,
          clientGroupSourceId: sourceGroup.id,
        },
    payload: chatMessage.payload.toJSON(),
    id: clientUuid,
    groupId: targetGroupId,
    senderId: me.id,
    clientTime: (dateNow + index).toString(),
    serverTime: (dateNow + index).toString(),
    editTime: '',
    usersReactions: {
      usersReactions: {},
    },
    links: [],
    views: '0',
  };
}

/**
 * Returns transformed data for forward message request and adds transformed fake chat messages to groups before the request
 * @param messageIds a message which is being forwarded
 * @param sourceGroupId id of a group from a massage is being forwarded
 * @param targetGroupIds ids of groups where a massage is being forwarded
 * @returns object with hashMessageIds and messagesToForward
 */
function processForwardMessagesData(
  messageIds: string[],
  sourceGroupId: string,
  targetGroupIds: string[]
): {
  hashMessageIds: Record<string, string>;
  messagesToForward: IForwardRequest['messagesToForward'];
} {
  const clientTime = dateService.now().getTime() - messageIds.length;
  const sourceGroup = Entities.GroupsStore.getGroupById(sourceGroupId);
  const activeGroup = Entities.GroupsStore.activeGroup;
  const hashMessageIds: Record<string, string> = {};
  const dateNow = Date.now();

  const messagesToForward: IForwardRequest['messagesToForward'] = [];

  targetGroupIds.forEach((targetGroupId: string) => {
    const messages: IMessage[] = [];
    const targetGroup = Entities.GroupsStore.getGroupById(targetGroupId);
    const lastMessageServerTime = targetGroup?.lastMessage?.expectedServerTime || dateNow;

    messageIds
      .map((messageId) => sourceGroup?.messagesStore.getMessageById(messageId))
      .filter((message: ChatMessage | null) => message?.id)
      .sort((a: ChatMessage, b: ChatMessage) => a.expectedServerTime - b.expectedServerTime)
      .forEach((message: ChatMessage, index) => {
        const clientUuid = uuidv4();
        hashMessageIds[getHashMessageIdsKey(targetGroupId, message.id)] = clientUuid;

        const forwardedMessage = mapChatMessageToForwardedMessage(
          message,
          index,
          sourceGroup,
          targetGroupId,
          clientUuid,
          dateNow
        );

        if (forwardedMessage) {
          messages.push(forwardedMessage);
        }

        messagesToForward.push({
          clientTs: clientTime + index,
          clientUuid: clientUuid,
          sourceMessageId: message.id,
          targetGroupId,
        });
      });

    updateOldOrFakeMessages({
      messages,
      groupId: targetGroupId,
      prevMessageTs: lastMessageServerTime,
    });

    if (activeGroup?.id === targetGroupId) {
      activeGroup.messagesStore.setNewMessagesPlaceholderTs(0);
      activeGroup.counter!.setSeenTs(dateNow);

      Entities.ChatStore.setScrollChanged(UpdateReason.ScrollToBottom, { isAnimated: true });
    }
  });

  return { messagesToForward, hashMessageIds };
}

/**
 * Forwards selected messages to selected groups
 * @param messageIds message ids array for forward
 * @param sourceGroupId id of a group from a massage is being forwarded
 * @param targetGroupIds ids of groups where a massage is being forwarded
 * @returns Promise with request result
 */
export async function forwardMessages(
  messageIds: string[],
  sourceGroupId: string,
  targetGroupIds: string[]
): Promise<boolean> {
  const { hashMessageIds, messagesToForward } = processForwardMessagesData(
    messageIds,
    sourceGroupId,
    targetGroupIds
  );

  if (!messagesToForward.length) {
    return false;
  }

  //  Set of clientUuid + groupId
  const createdMessagesUUID = new Set<string>();
  let result = true;

  try {
    const { forwardedMessages } = await messageModel.forwardMessage({
      sourceGroupId,
      messagesToForward,
    });

    forwardedMessages.forEach(({ groupId, messages, lastMessageTs }) => {
      Tasks.messaging.updateOldOrFakeMessages({
        messages,
        groupId,
        prevMessageTs: Number(lastMessageTs),
      });

      messages.forEach((message) =>
        createdMessagesUUID.add(getCreatedMessagesUUIDKey(groupId, message.clientUuid))
      );
    });
  } catch {
    result = false;
  }

  targetGroupIds.forEach((targetGroupId) => {
    const targetGroup = Entities.GroupsStore.getGroupById(targetGroupId);

    messageIds.forEach((messageId) => {
      const clientUuid = hashMessageIds[getHashMessageIdsKey(targetGroupId, messageId)];
      if (!createdMessagesUUID.has(getCreatedMessagesUUIDKey(targetGroupId, clientUuid))) {
        const message = targetGroup?.messagesStore.getMessageById(clientUuid);
        message?.isFake ? message?.setError(MessageError.Forward) : message?.resetError();
      }
    });
  });

  return result;
}
